/**
 * Plantedatoer, «Start ny runde» og redigering av historikken.
 *
 * Bytte mellom drift og testmodus endrer INGEN datoer. Å starte på nytt er en
 * egen handling: i drift legges plantingene slik de sto i historikken, og de
 * samme plantene settes inn på nytt med dato i dag. Historikken kan redigeres
 * og slettes i ettertid.
 */
import { supabase } from './supabase';

/** ISO-tid → «ÅÅÅÅ-MM-DD» i lokal tid, klar for <input type="date">. */
export function isoTilDato(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * «ÅÅÅÅ-MM-DD» → ISO-tid. Er dagen den samme som i `forrige`, beholdes den
 * opprinnelige tiden, så en lagring uten endring ikke flytter noe. En ny dag
 * settes til kl. 12 lokal tid, godt unna midnatt og tidssone-skifter.
 */
export function datoTilIso(dato: string, forrige?: string | null): string {
  if (forrige && isoTilDato(forrige) === dato) return forrige;
  const [a, m, d] = dato.split('-').map(Number);
  return new Date(a, m - 1, d, 12, 0, 0).toISOString();
}

/** Feilmelding for datoene til en planting, eller null når de er gyldige. */
export function sjekkDatoer(plantet: string, fjernet: string | null, naa = new Date()): string | null {
  if (!plantet) return 'Velg en plantedato.';
  const idag = isoTilDato(naa.toISOString());
  if (plantet > idag) return 'Plantedatoen kan ikke være frem i tid.';
  if (fjernet !== null) {
    if (!fjernet) return 'Velg når planten ble fjernet.';
    if (fjernet > idag) return 'Datoen den ble fjernet, kan ikke være frem i tid.';
    if (fjernet < plantet) return 'Planten kan ikke være fjernet før den ble plantet.';
  }
  return null;
}

type Resultat = { ok: true; antall: number } | { ok: false; melding: string };

/** Ny runde utføres i én databasetransaksjon, med modus fra den åpne kassa. */
export async function startNyRunde(potteId: string, iDrift: boolean, naa = new Date()): Promise<Resultat> {
  try {
    const { data, error } = await supabase.rpc('potte_start_ny_runde', {
      p_potte_id: potteId, p_forventet_drift: iDrift, p_tid: naa.toISOString(),
      p_tidssone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if (error || data === null) return { ok: false, melding: 'Kunne ikke bekrefte ny runde. Last inn kassa på nytt før du prøver igjen.' };
    return { ok: true, antall: data };
  } catch {
    return { ok: false, melding: 'Mistet forbindelsen. Last inn kassa på nytt før du prøver igjen.' };
  }
}

/** Forventet planting hindrer at et forsinket valg overskriver et nyere valg. */
export async function byttPlante(potteId: string, seksjon: number, planteId: string, forventetId: string | null, iDrift: boolean): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('potte_bytt_plante', {
      p_potte_id: potteId, p_seksjon: seksjon, p_plante_id: planteId,
      p_forventet_id: forventetId, p_forventet_drift: iDrift,
    });
    return !error && typeof data === 'string';
  } catch { return false; }
}

/** Kasse og tilhørende databaserader slettes samlet. Kamerafiler beholdes. */
export async function slettKasse(id: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('potte_slett_kasse', { p_id: id });
    return !error && data === true;
  } catch { return false; }
}

/** Lagre endrede datoer og notat på en planting (aktiv eller i historikken). */
export async function lagrePlanting(
  id: string,
  felt: { plantet_at?: string; fjernet_at?: string; notater?: string | null },
): Promise<boolean> {
  const { data, error } = await supabase.from('potte_planter').update(felt).eq('id', id).select('id');
  return !error && data?.length === 1;
}

/** Slett en planting fra historikken for godt. Rører aldri en aktiv planting. */
export async function slettFraHistorikk(id: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('potte_planter')
    .delete()
    .eq('id', id)
    .not('fjernet_at', 'is', null)
    .select('id');
  return !error && data?.length === 1;
}
